import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import {
  User as FirebaseUser,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth'
import { auth } from '@/config/firebase'
import { User, AuthState } from '@/types/user'
import { useToast } from '@/contexts/ToastContext'
import { useCallable } from '@/hooks/useCallable'

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  loginWithGoogle: (roleId?: number) => Promise<void>
  switchRole: (roleId: number) => Promise<void>
  isAdmin: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const mapFirebaseUser = (firebaseUser: FirebaseUser, roleId?: number, userId?: string): User => ({
  uid: firebaseUser.uid,
  email: firebaseUser.email,
  displayName: firebaseUser.displayName,
  photoURL: firebaseUser.photoURL,
  emailVerified: firebaseUser.emailVerified,
  roleId: roleId,
  userId: userId,
})

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { showError } = useToast()
  const callRegisterUser = useCallable('register_user')
  const callGetUserInfo  = useCallable('get_user_info')
  const callSwitchRole   = useCallable('switch_role')

  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  })

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setState({ user: null, loading: false, error: null })
        return
      }

      // localStorageの値は初期表示用の暫定値。権限判定に使える値ではないため
      // (DevToolsから書き換え可能)、必ずサーバーの値で上書きする。
      const cachedRoleId = localStorage.getItem(`role_${firebaseUser.uid}`)
      const cachedUserId = localStorage.getItem(`userId_${firebaseUser.uid}`)
      setState({
        user: mapFirebaseUser(
          firebaseUser,
          cachedRoleId ? parseInt(cachedRoleId) : undefined,
          cachedUserId ?? undefined,
        ),
        loading: true,
        error: null,
      })

      const infoRes = await callGetUserInfo()
      const infoData = infoRes?.data as { roleId?: number; userId?: string } | undefined
      if (infoData?.userId) {
        localStorage.setItem(`userId_${firebaseUser.uid}`, infoData.userId)
      }
      if (infoData?.roleId) {
        localStorage.setItem(`role_${firebaseUser.uid}`, String(infoData.roleId))
      }
      setState({
        user: mapFirebaseUser(firebaseUser, infoData?.roleId, infoData?.userId),
        loading: false,
        error: null,
      })
    })

    return () => unsubscribe()
  }, [callGetUserInfo])

  const isAdmin = state.user?.roleId === 1

  const login = async (email: string, password: string) => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }))
      const result = await signInWithEmailAndPassword(auth, email, password)
      
      // role_idをlocalStorageから取得
      const roleIdStr = localStorage.getItem(`role_${result.user.uid}`)
      const roleId = roleIdStr ? parseInt(roleIdStr) : undefined
      
      setState({
        user: mapFirebaseUser(result.user, roleId),
        loading: false,
        error: null,
      })
    } catch (error) {
      const msg = (error as Error).message
      showError(msg)
      setState((prev) => ({ ...prev, loading: false, error: msg }))
      throw error
    }
  }

  const register = async (email: string, password: string) => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }))
      await createUserWithEmailAndPassword(auth, email, password)
    } catch (error) {
      const msg = (error as Error).message
      showError(msg)
      setState((prev) => ({ ...prev, loading: false, error: msg }))
      throw error
    }
  }

  const logout = async () => {
    try {
      // localStorageに保存しているこのユーザーの認証関連情報を削除
      // (role_idのみ削除してuserId_を残すと、再ログイン時や画面更新時に
      //  前のアカウント情報が復元されてしまうため両方削除する)
      if (state.user?.uid) {
        localStorage.removeItem(`role_${state.user.uid}`)
        localStorage.removeItem(`userId_${state.user.uid}`)
      }
      await signOut(auth)
      setState({
        user: null,
        loading: false,
        error: null,
      })
    } catch (error) {
      const msg = (error as Error).message
      showError(msg)
      setState((prev) => ({ ...prev, error: msg }))
      throw error
    }
  }

  const loginWithGoogle = async (newUserRoleId: number = 1) => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }))
      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(auth, provider)

      // IDトークンを強制リフレッシュしてからCallableを呼ぶ
      await result.user.getIdToken(true)

      // Step1: ユーザー登録 or 更新（存在チェック＋upsert）
      let roleId: number | undefined
      const registerRes = await callRegisterUser({ roleId: newUserRoleId })
      if (registerRes) {
        const registerData = registerRes.data as { roleId?: number; isNewUser?: boolean }
        roleId = registerData.roleId  // Step2が失敗した場合のフォールバック
      }

      // Step2: t_user_infoからメールアドレスで最新情報を取得
      let userId: string | undefined
      const infoRes = await callGetUserInfo()
      if (infoRes) {
        const infoData = infoRes.data as { roleId?: number; userId?: string }
        roleId = infoData.roleId
        userId = infoData.userId
      }

      // role_id / user_idを含めてstateを更新
      setState({
        user: mapFirebaseUser(result.user, roleId, userId),
        loading: false,
        error: null,
      })

      // role_id / user_idをlocalStorageに保存（初期表示用のキャッシュ）
      if (roleId) {
        localStorage.setItem(`role_${result.user.uid}`, roleId.toString())
      }
      if (userId) {
        localStorage.setItem(`userId_${result.user.uid}`, userId)
      }
    } catch (error) {
      const msg = (error as Error).message
      showError(msg)
      setState((prev) => ({ ...prev, loading: false, error: msg }))
      throw error
    }
  }

  const switchRole = async (newRoleId: number) => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }))
      const res = await callSwitchRole({ roleId: newRoleId })
      if (res) {
        const data = res.data as { roleId?: number }
        const roleId = data.roleId ?? newRoleId
        setState((prev) => ({
          ...prev,
          user: prev.user ? { ...prev.user, roleId } : null,
          loading: false,
          error: null,
        }))
        // localStorageも更新
        if (state.user?.uid) {
          localStorage.setItem(`role_${state.user.uid}`, roleId.toString())
        }
      }
    } catch (error) {
      const msg = (error as Error).message ?? 'ロール切替に失敗しました'
      showError(msg)
      setState((prev) => ({ ...prev, loading: false, error: msg }))
      throw error
    }
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        register,
        logout,
        loginWithGoogle,
        switchRole,
        isAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
